import { restClient } from '@/Core'
import type { AttachmentType } from '../Contracts'

/** Response of POST /services/chat/files (backend ChatFilePresenter::present) */
export interface UploadedChatFile {
    uuid: string
    type: AttachmentType
    name: string
    size: number
    url: string
}

/**
 * Step 1 of the two-step attachment flow: uploads a raw file to the chat
 * storage and returns its uuid plus a signed download url. The file stays
 * "unattached" until step 2 — a message sent with fileUuids picks it up
 * (orphans are garbage-collected by the backend after 24 hours).
 *
 * The Content-Type header is left to the browser: multipart/form-data needs
 * the generated boundary, so the default application/ld+json of restClient
 * is suppressed with isNotHeaderAddContentType.
 */
export const UploadChatFileTask = async (file: File): Promise<UploadedChatFile> => {
    const body = new FormData()
    body.append('file', file, file.name)

    const response = (await restClient.post(
        '/services/chat/files',
        { body },
        { isNotHeaderAddContentType: true },
    )) as Partial<UploadedChatFile> | null

    if (!response?.uuid) {
        throw new Error('File upload failed')
    }

    return {
        uuid: response.uuid,
        type: response.type ?? 'file',
        name: response.name ?? file.name,
        size: response.size ?? file.size,
        url: response.url ?? '',
    }
}
